import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const getMe = () => api.get('/auth/me');
export const changePassword = (data) => api.put('/auth/password', data);
export const updateProfile = (data) => api.put('/auth/profile', data);

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const resetUserPassword = (id, password) => api.put(`/users/${id}/password`, { password });
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Companies
export const getCompaniesAll = () => api.get('/companies');
export const getCompany = (id) => api.get(`/companies/${id}`);
export const createCompany = (data) => api.post('/companies', data);
export const updateCompany = (id, data) => api.put(`/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/companies/${id}`);

// Vendors
export const getVendors = (params) => api.get('/vendors', { params });
export const getVendor = (id) => api.get(`/vendors/${id}`);
export const createVendor = (data) => api.post('/vendors', data);
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data);
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);

// Customer-Company Links
export const getCustomerCompanies = (customerId) => api.get(`/customers/${customerId}/companies`);
export const linkCustomerCompany = (customerId, companyId) =>
  api.post(`/customers/${customerId}/companies`, { company_id: companyId });
export const unlinkCustomerCompany = (customerId, companyId) =>
  api.delete(`/customers/${customerId}/companies/${companyId}`);

// Customer Contracts
export const getCustomerContracts = (customerId) => api.get(`/customers/${customerId}/contracts`);
export const uploadCustomerContract = (customerId, formData) =>
  api.post(`/customers/${customerId}/contracts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const updateCustomerContract = (customerId, contractId, data) =>
  api.put(`/customers/${customerId}/contracts/${contractId}`, data);
export const deleteCustomerContract = (customerId, contractId) =>
  api.delete(`/customers/${customerId}/contracts/${contractId}`);

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Employees
export const getEmployees = (params) => api.get('/employees', { params });
export const getEmployee = (id) => api.get(`/employees/${id}`);
export const getManagersLookup = (params) => api.get('/employees/lookup/managers', { params });
export const getMyProfile = () => api.get('/employees/me/profile');
export const getMyDirectReports = () => api.get('/employees/me/direct-reports');
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);
export const uploadEmployeeDocument = (id, formData) =>
  api.post(`/employees/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const deleteEmployeeDocument = (id, docId) => api.delete(`/employees/${id}/documents/${docId}`);
export const getEmployeeHistory = (id) => api.get(`/employees/${id}/history`);
export const addEmploymentPeriod = (id, data) => api.post(`/employees/${id}/history`, data);
export const updateEmploymentPeriod = (id, historyId, data) =>
  api.put(`/employees/${id}/history/${historyId}`, data);
export const deleteEmploymentPeriod = (id, historyId) =>
  api.delete(`/employees/${id}/history/${historyId}`);

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== null && data[key] !== undefined) {
      if (key === 'line_items') formData.append(key, JSON.stringify(data[key]));
      else formData.append(key, data[key]);
    }
  });
  return api.post('/invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateInvoice = (id, data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== null && data[key] !== undefined) {
      if (key === 'line_items') formData.append(key, JSON.stringify(data[key]));
      else formData.append(key, data[key]);
    }
  });
  return api.put(`/invoices/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const submitInvoice = (id) => api.post(`/invoices/${id}/submit`);
export const approveInvoice = (id, comment) => api.post(`/invoices/${id}/approve`, { comment });
export const reworkInvoice = (id, comment) => api.post(`/invoices/${id}/rework`, { comment });
export const rejectInvoice = (id, comment) => api.post(`/invoices/${id}/reject`, { comment });
export const sendToAccounting = (id) => api.post(`/invoices/${id}/send-to-accounting`);
export const markInvoicePaid = (id, data) => api.post(`/invoices/${id}/mark-paid`, data);
export const markInvoiceUnpaid = (id) => api.post(`/invoices/${id}/mark-unpaid`);
export const exportInvoices = (params) => api.get('/invoices/export/excel', { params, responseType: 'blob' });

// AR Invoices
export const getARInvoices = (params) => api.get('/ar-invoices', { params });
export const getARInvoice = (id) => api.get(`/ar-invoices/${id}`);
export const createARInvoice = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== null && data[key] !== undefined) {
      if (key === 'line_items') formData.append(key, JSON.stringify(data[key]));
      else formData.append(key, data[key]);
    }
  });
  return api.post('/ar-invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateARInvoice = (id, data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== null && data[key] !== undefined) {
      if (key === 'line_items') formData.append(key, JSON.stringify(data[key]));
      else formData.append(key, data[key]);
    }
  });
  return api.put(`/ar-invoices/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteARInvoice = (id) => api.delete(`/ar-invoices/${id}`);
export const submitARInvoice = (id) => api.post(`/ar-invoices/${id}/submit`);
export const approveARInvoice = (id, comment) => api.post(`/ar-invoices/${id}/approve`, { comment });
export const reworkARInvoice = (id, comment) => api.post(`/ar-invoices/${id}/rework`, { comment });
export const rejectARInvoice = (id, comment) => api.post(`/ar-invoices/${id}/reject`, { comment });
export const sendARToAccounting = (id) => api.post(`/ar-invoices/${id}/send-to-accounting`);
export const markARInvoiceReceived = (id, data) => api.post(`/ar-invoices/${id}/mark-received`, data);
export const markARInvoiceNotReceived = (id) => api.post(`/ar-invoices/${id}/mark-not-received`);
export const exportARInvoices = (params) => api.get('/ar-invoices/export/excel', { params, responseType: 'blob' });

// Cost Centers
export const getCostCentersAll = () => api.get('/cost-centers');
export const getCostCenter = (id) => api.get(`/cost-centers/${id}`);
export const createCostCenter = (data) => api.post('/cost-centers', data);
export const updateCostCenter = (id, data) => api.put(`/cost-centers/${id}`, data);
export const deleteCostCenter = (id) => api.delete(`/cost-centers/${id}`);

// Accounts (Chart of Accounts)
export const getAccountsAll = () => api.get('/accounts');
export const getAccount = (id) => api.get(`/accounts/${id}`);
export const createAccount = (data) => api.post('/accounts', data);
export const updateAccount = (id, data) => api.put(`/accounts/${id}`, data);
export const deleteAccount = (id) => api.delete(`/accounts/${id}`);

// Lookups
export const getCompanies = () => api.get('/lookups/companies');
export const getCostCenters = () => api.get('/lookups/cost-centers');
export const getAccounts = () => api.get('/lookups/accounts');
export const getBankAccounts = () => api.get('/lookups/bank-accounts');
export const getVendorsList = () => api.get('/lookups/vendors');
export const getCustomersList = () => api.get('/customers/list/dropdown');
export const getProjectsList = () => api.get('/lookups/projects');
export const getProducts = () => api.get('/lookups/products');
export const getCurrencies = () => api.get('/lookups/currencies');
export const getPaymentTypes = () => api.get('/lookups/payment-types');
export const getCountries = () => api.get('/lookups/countries');
export const getDashboard = () => api.get('/lookups/dashboard');
export const getAPInvoicesDashboard = () => api.get('/lookups/ap-invoices-dashboard');

// Foreign Exchange Rates
export const getFXRates = (date) => api.get('/fx-rates', { params: { date } });
export const getFXDates = () => api.get('/fx-rates/dates');
export const updateFXRates = () => api.post('/fx-rates/update');
export const updateFXRatesRange = (from_date, to_date) => api.post('/fx-rates/update-range', { from_date, to_date });

// Banks
export const getBanks = (params) => api.get('/banks', { params });
export const getBank = (id) => api.get(`/banks/${id}`);
export const createBank = (data) => api.post('/banks', data);
export const updateBank = (id, data) => api.put(`/banks/${id}`, data);
export const deleteBank = (id) => api.delete(`/banks/${id}`);

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const exportAuditLogs = (params) => api.get('/audit-logs/export/excel', { params, responseType: 'blob' });

// Channel Reports
export const getChannelReports = (params) => api.get('/channel-reports', { params });
export const getChannelReport = (id) => api.get(`/channel-reports/${id}`);
export const uploadChannelReport = (formData) =>
  api.post('/channel-reports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const deleteChannelReport = (id) => api.delete(`/channel-reports/${id}`);

// Channels
export const getChannels = (params) => api.get('/channels', { params });
export const getChannel = (id) => api.get(`/channels/${id}`);
export const updateChannel = (id, data) => api.put(`/channels/${id}`, data);
export const getChannelCustomers = () => api.get('/channels/lookup/customers');
export const getChannelProjects = () => api.get('/channels/lookup/projects');

// Customer channels and projects
export const getCustomerChannels = (customerId) => api.get(`/customers/${customerId}/channels`);
export const getCustomerProjects = (customerId) => api.get(`/customers/${customerId}/projects`);

export default api;
